import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import PageTitle from './PageTitle';

const pageTitles = {
  '/about': {
    zh: '關於 Beat Buddy',
    en: 'About Beat Buddy',
  },
  '/contact': {
    zh: '聯絡 Beat Buddy',
    en: 'Contact Beat Buddy',
  },
  '/features': {
    zh: 'Beat Buddy 功能介紹',
    en: 'Beat Buddy Features',
  },
  '/help': {
    zh: 'Beat Buddy 使用幫助',
    en: 'Beat Buddy Help',
  },
  '/privacy': {
    zh: 'Beat Buddy 隱私政策',
    en: 'Beat Buddy Privacy Policy',
  },
  '/terms': {
    zh: 'Beat Buddy 使用條款',
    en: 'Beat Buddy Terms of Use',
  },
  '*': {
    zh: '找不到頁面',
    en: 'Page Not Found',
  },
};

const editorialContent = {
  '/about': {
    zh: {
      question: 'Beat Buddy 是什麼？',
      answer: 'Beat Buddy 是一款免費互動式節拍器，透過角色、成就與視聽回饋，讓節奏練習更有趣。',
    },
    en: {
      question: 'What is Beat Buddy?',
      answer: 'Beat Buddy is a free interactive metronome that makes rhythm practice engaging with characters, achievements, and audiovisual feedback.',
    },
  },
  '/contact': {
    zh: {
      question: '如何聯絡 Beat Buddy 團隊？',
      answer: '您可以透過電子郵件聯絡團隊，查詢使用問題、回報錯誤或提出功能建議。',
    },
    en: {
      question: 'How can I contact the Beat Buddy team?',
      answer: 'Contact the team by email for usage questions, bug reports, or feature suggestions.',
    },
  },
  '/features': {
    zh: {
      question: 'Beat Buddy 提供哪些節拍練習功能？',
      answer: 'Beat Buddy 提供 40–240 BPM、不同拍號、自訂重音、互動角色、成就與本機練習統計。',
    },
    en: {
      question: 'What rhythm practice features does Beat Buddy offer?',
      answer: 'Beat Buddy offers 40–240 BPM, multiple time signatures, custom accents, interactive characters, achievements, and local practice statistics.',
    },
  },
};

const EditorialDates = ({ language }) => (
  <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-purple-100 pt-3 text-xs font-medium text-gray-500 dark:border-gray-700 dark:text-gray-400">
    <span>
      {language === 'zh' ? '發布日期：' : 'Published: '}
      <time dateTime="2025-01-15">
        {language === 'zh' ? '2025 年 1 月 15 日' : 'January 15, 2025'}
      </time>
    </span>
    <span>
      {language === 'zh' ? '更新日期：' : 'Updated: '}
      <time dateTime="2026-03-07">
        {language === 'zh' ? '2026 年 3 月 7 日' : 'March 7, 2026'}
      </time>
    </span>
  </p>
);

const ContentPageMeta = () => {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const localizedLanguage = language === 'en' ? 'en' : 'zh';
  const titles = pageTitles[pathname] || pageTitles['*'];
  const editorial = editorialContent[pathname]?.[localizedLanguage];

  return (
    <header className="mb-6">
      <PageTitle className="sr-only">
        {titles[localizedLanguage]}
      </PageTitle>

      {editorial && (
        <section
          className="rounded-2xl border border-purple-100 bg-white/70 px-5 py-4 shadow-sm backdrop-blur-sm dark:border-purple-900/50 dark:bg-gray-800/70"
          aria-labelledby="editorial-question"
        >
          <h2
            id="editorial-question"
            className="text-base font-semibold text-gray-800 dark:text-gray-100"
          >
            {editorial.question}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
            {editorial.answer}
          </p>
          <EditorialDates language={localizedLanguage} />
        </section>
      )}
    </header>
  );
};

export default ContentPageMeta;